import { useEffect, useState } from 'react'
import {
  Circle,
  CircleMarker,
  MapContainer,
  Marker,
  Polygon,
  Polyline,
  Popup,
  Rectangle,
  TileLayer,
  Tooltip,
  WMSTileLayer,
  useMap,
  useMapEvents,
} from 'react-leaflet'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { layers as fetchLayers } from '../api/endpoints'
import type { FeatureEdits } from '../import/featureEdit'
import { distance, type LatLng } from '../import/geo'
import type { DrawTool } from '../import/draw'
import type {
  MapFeature,
  MapLayer,
  Mission,
  MissionTeam,
  Participant,
  Position,
} from '../api/types'

/** APP-6 affiliation colours, the same values the app uses. Fill means affiliation, never team. */
const AFFILIATION: Record<string, string> = {
  FRIEND: '#5b8fe0',
  ASSUMED_FRIEND: '#5b8fe0',
  HOSTILE: '#e24a42',
  SUSPECT: '#e24a42',
  NEUTRAL: '#4fa85e',
  UNKNOWN: '#e5c94b',
  PENDING: '#e5c94b',
}

/**
 * The whole picture, every faction at once.
 *
 * This is the one view in the product that deliberately ignores the visibility rules players are
 * subject to — because the server has already decided the organiser may see across factions, and a
 * TOC that could only see one side would be useless for refereeing.
 */
export function TocMap({
  mission,
  teams,
  roster,
  positions,
  features,
  placing,
  onPick,
  selectedId,
  edits,
  editable,
  onSelect,
  onReshape,
  drawing,
  onDrawClick,
  focus,
}: {
  mission: Mission
  teams: MissionTeam[]
  roster: Participant[]
  positions: Position[]
  features: MapFeature[]
  placing: boolean
  onPick: (latlng: [number, number]) => void
  /** The feature open in the editor, drawn from [edits] rather than from the server's copy. */
  selectedId?: string | null
  edits?: FeatureEdits | null
  editable?: boolean
  onSelect?: (featureId: string | null) => void
  onReshape?: (positions: FeatureEdits['positions']) => void
  /** A shape being drawn: clicks add points, and a dashed preview follows them. */
  drawing?: { tool: DrawTool; points: LatLng[] } | null
  onDrawClick?: (point: LatLng) => void
  /** Somewhere to fly to, e.g. a report's position. The key makes a repeat request fly again. */
  focus?: { at: LatLng; key: number } | null
}) {
  const [available, setAvailable] = useState<MapLayer[]>([])
  const [chosen, setChosen] = useState<string | null>(null)
  // Names on the map: on by default, because a dot nobody can name is a dot nobody can act on.
  // Off for a crowded map, where the labels start covering the things they name.
  const [labels, setLabels] = useState(true)

  useEffect(() => {
    void (async () => {
      const result = await fetchLayers()
      if (!result.ok) return
      const bases = result.value.filter((layer) => !layer.overlay)
      setAvailable(bases)
      setChosen(
        mission.defaultLayerId ??
          bases.find((layer) => layer.defaultLayer)?.id ??
          bases[0]?.id ??
          null,
      )
    })()
  }, [mission.defaultLayerId])

  const base = available.find((layer) => layer.id === chosen)
  const centre: [number, number] = [
    mission.centreLatitude ?? 45.5416,
    mission.centreLongitude ?? 10.2118,
  ]

  return (
    <div
      className={drawing || placing ? 'map-drawing' : undefined}
      style={{ position: 'absolute', inset: 0 }}
    >
      <MapContainer
        center={centre}
        zoom={mission.initialZoom ?? 15}
        style={{ height: '100%', width: '100%' }}
        // North stays up here too: a rotated map is one you cannot take a bearing from, and the
        // organiser is reading grids to people over a radio.
        zoomControl
      >
        {base &&
          (base.type === 'WMS' ? (
            // The Italian IGM sheets are WMS, published split by UTM zone; naming both layers in
            // one request makes the server composite them so the zone boundary never arrives here.
            <WMSTileLayer
              url={base.url}
              layers={base.wmsLayers ?? ''}
              format="image/png"
              transparent={false}
              attribution={base.attribution ?? ''}
              maxNativeZoom={base.maxZoom}
              maxZoom={22}
            />
          ) : (
            <TileLayer
              url={base.url}
              attribution={base.attribution ?? ''}
              maxNativeZoom={base.maxZoom}
              maxZoom={22}
            />
          ))}

        <ClickCatcher
          enabled={placing}
          onPick={onPick}
          drawing={drawing != null}
          onDrawClick={onDrawClick}
          onEmptyClick={() => onSelect?.(null)}
        />
        <FocusController focus={focus ?? null} />
        {drawing && <DraftPreview tool={drawing.tool} points={drawing.points} />}

        {/* Drawn features, under the people. A plan is less urgent than a person. */}
        {features.map((feature) => {
          const selected = feature.id === selectedId && edits != null
          return (
            <Feature
              labels={labels}
              key={feature.id}
              feature={
                selected
                  ? { ...feature, ...edits, colorHex: edits.colorHex ?? undefined }
                  : feature
              }
              selected={selected}
              editable={selected && !!editable}
              onSelect={() => onSelect?.(feature.id)}
              onReshape={onReshape}
            />
          )
        })}

        {positions.map((position) => {
          const person = roster.find((entry) => entry.userId === position.userId)
          const team = teams.find((entry) => entry.teamId === position.teamId)
          const colour = team?.colorHex ?? person?.teamColorHex ?? '#7cc576'

          return (
            <CircleMarker
              key={position.userId}
              center={[position.latitude, position.longitude]}
              radius={6}
              pathOptions={{
                color: '#0d1210',
                weight: 2,
                // Faded rather than hidden once stale: where somebody was is worth knowing, as
                // long as nobody reads it as where they are.
                fillColor: colour,
                fillOpacity: position.stale ? 0.35 : 0.95,
              }}
            >
              {labels && person && (
                <Tooltip permanent direction="right" offset={[8, 0]} className="map-label">
                  {person.callsign}
                </Tooltip>
              )}
              <Popup>
                <strong>{person?.callsign ?? 'unknown'}</strong>
                <br />
                {team?.teamName ?? 'no team'}
                <br />
                {position.stale ? 'Last known position' : 'Live'}
                {position.batteryPercent != null && (
                  <>
                    <br />
                    Battery {position.batteryPercent}%
                  </>
                )}
              </Popup>
            </CircleMarker>
          )
        })}
      </MapContainer>

      <div className="map-toolbar">
        <button
          className="toggle"
          aria-pressed={labels}
          onClick={() => setLabels((value) => !value)}
        >
          Labels
        </button>
        {available.length > 0 && (
          <select
            aria-label="Map layer"
            value={chosen ?? ''}
            onChange={(event) => setChosen(event.target.value)}
          >
            {available.map((layer) => (
              <option key={layer.id} value={layer.id}>
                {layer.name}
              </option>
            ))}
          </select>
        )}
      </div>
    </div>
  )
}

function Feature({
  feature,
  labels,
  selected,
  editable,
  onSelect,
  onReshape,
}: {
  feature: MapFeature
  labels: boolean
  selected: boolean
  editable: boolean
  onSelect: () => void
  onReshape?: (positions: FeatureEdits['positions']) => void
}) {
  const colour = feature.colorHex ?? AFFILIATION[feature.affiliation] ?? '#7cc576'
  const points = feature.positions.map((p) => [p.latitude, p.longitude] as [number, number])
  if (points.length === 0) return null

  const select = { click: () => onSelect() }
  const name =
    labels && feature.label ? (
      <Tooltip
        permanent
        direction={feature.geometryType === 'POINT' ? 'right' : 'center'}
        offset={feature.geometryType === 'POINT' ? [10, 0] : [0, 0]}
        className="map-label"
      >
        {feature.label}
      </Tooltip>
    ) : null
  // Selected: thicker, with a light halo, so it reads as picked out against any map.
  const weight = selected ? 4 : 2
  const halo = selected ? { color: '#ffffff', weight: weight + 4, opacity: 0.55, fill: false } : null

  const moveVertex = (index: number, lat: number, lng: number) =>
    onReshape?.(
      feature.positions.map((p, i) => (i === index ? { latitude: lat, longitude: lng } : p)),
    )

  // Corner handles, for reshaping. A circle has one, at its centre; its radius is a number in the
  // editor, because dragging an edge to "about 250 metres" is less use than typing 250.
  const handles = editable
    ? (feature.geometryType === 'CIRCLE' ? points.slice(0, 1) : points).map((point, index) => (
        <Marker
          key={`h-${index}-${point[0]}-${point[1]}`}
          position={point}
          draggable
          icon={HANDLE}
          eventHandlers={{
            dragend: (event) => {
              const at = (event.target as L.Marker).getLatLng()
              moveVertex(index, at.lat, at.lng)
            },
          }}
        />
      ))
    : null

  if (feature.geometryType === 'POINT') {
    return (
      <Marker
        position={points[0]}
        icon={pointIcon(colour, feature.objective, selected)}
        draggable={editable}
        eventHandlers={{
          ...select,
          dragend: (event) => {
            const at = (event.target as L.Marker).getLatLng()
            moveVertex(0, at.lat, at.lng)
          },
        }}
        title={feature.label ?? undefined}
      >
        {name}
      </Marker>
    )
  }

  if (feature.geometryType === 'CIRCLE' && feature.radiusMetres) {
    // In metres, not pixels. A range ring that stays the same size on screen as the map zooms is
    // a dot, not a range.
    return (
      <>
        {halo && <Circle center={points[0]} radius={feature.radiusMetres} pathOptions={halo} />}
        <Circle
          center={points[0]}
          radius={feature.radiusMetres}
          pathOptions={{ color: colour, fillOpacity: 0.12, weight }}
          eventHandlers={select}
        >
          {name}
        </Circle>
        {handles}
      </>
    )
  }

  if (feature.geometryType === 'POLYGON' || feature.geometryType === 'RECTANGLE') {
    return (
      <>
        {halo && <Polygon positions={points} pathOptions={halo} />}
        <Polygon
          positions={points}
          pathOptions={{ color: colour, fillOpacity: 0.15, weight }}
          eventHandlers={select}
        >
          {name}
        </Polygon>
        {handles}
      </>
    )
  }

  return (
    <>
      {halo && <Polyline positions={points} pathOptions={halo} />}
      <Polyline
        positions={points}
        pathOptions={{
          color: colour,
          weight: weight + 1,
          dashArray: feature.geometryType === 'MEASUREMENT' ? '6 6' : undefined,
        }}
        eventHandlers={select}
      >
        {name}
      </Polyline>
      {handles}
    </>
  )
}

/** A square white handle, the convention for "drag this corner". */
const HANDLE = L.divIcon({
  className: 'vertex-handle',
  iconSize: [14, 14],
  iconAnchor: [7, 7],
})

/**
 * A marker drawn rather than bundled, so it can take the affiliation colour.
 *
 * Objectives get a ring so they stand out from ordinary markers at a glance, which is what a TOC
 * looks at the map for.
 */
function pointIcon(colour: string, objective: boolean, selected = false): L.DivIcon {
  const size = (objective ? 22 : 16) + (selected ? 6 : 0)
  return L.divIcon({
    className: '',
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    html: `<div style="
      width:${size}px;height:${size}px;border-radius:50%;
      background:${colour};
      border:2px solid #0d1210;
      box-shadow:${selected ? '0 0 0 3px #ffffff, 0 0 0 6px rgba(0,0,0,.35)' : objective ? `0 0 0 3px ${colour}55` : 'none'};
    "></div>`,
  })
}

function ClickCatcher({
  enabled,
  onPick,
  drawing,
  onDrawClick,
  onEmptyClick,
}: {
  enabled: boolean
  onPick: (latlng: [number, number]) => void
  drawing: boolean
  onDrawClick?: (point: LatLng) => void
  onEmptyClick: () => void
}) {
  useMapEvents({
    click(event) {
      // Drawing takes every click, on top of an existing shape included: a route is often drawn
      // along a boundary somebody else already marked.
      if (drawing) {
        onDrawClick?.([event.latlng.lat, event.latlng.lng])
        return
      }
      if (enabled) {
        onPick([event.latlng.lat, event.latlng.lng])
        return
      }
      // A click on a shape reaches the map too. Only a click on bare map closes the editor.
      const target = event.originalEvent.target as Element | null
      if (!target?.classList?.contains('leaflet-interactive')) onEmptyClick()
    },
  })
  return null
}

/** Flies to a requested position, once per request. */
function FocusController({ focus }: { focus: { at: LatLng; key: number } | null }) {
  const map = useMap()
  useEffect(() => {
    if (focus) map.flyTo(focus.at, Math.max(map.getZoom(), 17), { duration: 0.6 })
  }, [focus, map])
  return null
}

/** The shape as drawn so far, dashed so it never reads as saved. */
function DraftPreview({ tool, points }: { tool: DrawTool; points: LatLng[] }) {
  const style = { color: '#7cc576', weight: 3, dashArray: '6 6', fillOpacity: 0.12 }
  const vertices = points.map((point, index) => (
    <CircleMarker
      key={`v-${index}`}
      center={point}
      radius={5}
      pathOptions={{ color: '#0d1210', weight: 2, fillColor: '#ffffff', fillOpacity: 1 }}
      interactive={false}
    />
  ))

  let shape = null
  if (tool === 'ring' && points.length === 2) {
    shape = <Circle center={points[0]} radius={distance(points[0], points[1])} pathOptions={style} interactive={false} />
  } else if (tool === 'box' && points.length === 2) {
    shape = <Rectangle bounds={[points[0], points[1]]} pathOptions={style} interactive={false} />
  } else if (tool === 'area' && points.length >= 3) {
    shape = <Polygon positions={points} pathOptions={style} interactive={false} />
  } else if (points.length >= 2 && tool !== 'box' && tool !== 'ring') {
    shape = <Polyline positions={points} pathOptions={style} interactive={false} />
  }

  return (
    <>
      {shape}
      {vertices}
    </>
  )
}
